import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';

// Deep merge utility
const mergeConfigs = (base: any, platformOverride: any): any => {
  if (!platformOverride) { return base; }
  if (typeof base !== 'object' || base === null) { return platformOverride; }
  if (typeof platformOverride !== 'object' || platformOverride === null) { return platformOverride; }

  const merged: any = { ...base };

  for (const key of Object.keys(platformOverride)) {
    if (key in base && typeof base[key] === 'object' && typeof platformOverride[key] === 'object') {
      merged[key] = mergeConfigs(base[key], platformOverride[key]);
    } else {
      merged[key] = platformOverride[key];
    }
  }

  return merged;
};

export const useUIControls = () => {
  const { appConfig, featureFlags } = useConfigStore.getState();
  const currentLanguage = localStorage.getItem('jwapp.language');

  // Merge platform overrides for featureFlags
  const platformWebOverride = featureFlags?.platforms?.web || {};
  const mergedFlags = mergeConfigs(featureFlags, platformWebOverride);

  // -------- UI Controls (from appConfig, no overrides) --------
  const logoURL = appConfig?.branding?.logo_url;
  const buttonLabelOnly = appConfig?.ui_controls?.button_display_modes?.label_only;
  const buttonIconOnly = appConfig?.ui_controls?.button_display_modes?.icon_only;
  const buttonLabelWithIcon = appConfig?.ui_controls?.button_display_modes?.label_with_icon;
  const singleClickPlayEnabled = appConfig?.ui_controls?.single_click;

  const canShowConsentManagement = appConfig?.ui_controls?.can_show_consent_management ?? false;
  const cookiesDescription = currentLanguage === 'en' ? appConfig?.ui_controls?.cookies?.description?.en : appConfig?.ui_controls?.cookies?.description?.es;
  const cookiesButton = currentLanguage === 'en' ? appConfig?.ui_controls?.cookies?.button_label?.en : appConfig?.ui_controls?.cookies?.button_label?.es;
  const cookieConsentName = appConfig?.ui_controls?.cookies?.consent_cookie_name;
  const cookiesExpiredDays = appConfig?.ui_controls?.cookies?.consent_cookie_expiry_days;

  const subscriptionSuccessDescription = currentLanguage === 'en' ? appConfig?.ui_controls?.subscription?.payment_success_description?.en : appConfig?.ui_controls?.subscription?.payment_success_description?.es;
  const subscriptionFailedDescription = currentLanguage === 'en' ? appConfig?.ui_controls?.subscription?.payment_failed_description?.en : appConfig?.ui_controls?.subscription?.payment_failed_description?.es;
  const subscriptionCancelledDescription = currentLanguage === 'en' ? appConfig?.ui_controls?.subscription?.payment_cancelled_description?.en : appConfig?.ui_controls?.subscription?.payment_cancelled_description?.es;

  const mobileAppsLinksTitle = currentLanguage === 'en' ? appConfig?.footer?.mobile_apps_links?.title?.en : appConfig?.footer?.mobile_apps_links?.title?.es;

  // -------- Feature Flags (with overrides applied) --------
  const epgNoProgramText = mergedFlags?.feature_flags?.epg_settings?.epg_no_program_message;
  const epgEmptySlotText = mergedFlags?.feature_flags?.epg_settings?.epg_empty_slot_message;
  const epgDisplayHours = mergedFlags?.feature_flags?.epg_settings?.epg_display_hours;
  const epgTimeFormat = mergedFlags?.feature_flags?.epg_settings?.epg_time_format;
  const epgDefaultImage = mergedFlags?.feature_flags?.epg_settings?.epg_default_image;
  const epgDisabledMessage = mergedFlags?.feature_flags?.epg_disabled_message;
  const heroBanner = mergedFlags?.ui_settings?.hero_banner;

  const favoriteLimitation = mergedFlags?.cleeng?.max_no_of_fav;
  const continueWatchHistoryLimitation = mergedFlags?.cleeng?.max_no_of_continue_watching;
  const favoriteLimitationMessage = currentLanguage === 'en' ? mergedFlags?.cleeng?.max_fav_message?.en : mergedFlags?.cleeng?.max_fav_message?.es;

  // -------- Legal (with overrides applied) --------
  const redirectionPolicyURL = mergedFlags?.common?.legal_docs?.privacy_policy ?? '';
  const redirectionTosURL = mergedFlags?.common?.legal_docs?.terms_of_use ?? '';
  const redirectionAboutUsURL = mergedFlags?.common?.legal_docs?.external_docs?.about_us ?? '';
  const redirectionSupportURL = mergedFlags?.common?.legal_docs?.external_docs?.footer_support_link ?? '';

  return {
    // UI Controls
    buttonLabelOnly,
    buttonIconOnly,
    buttonLabelWithIcon,
    logoURL,
    singleClickPlayEnabled,
    canShowConsentManagement,
    cookiesDescription,
    cookiesButton,
    cookieConsentName,
    cookiesExpiredDays,
    subscriptionSuccessDescription,
    subscriptionCancelledDescription,
    subscriptionFailedDescription,
    mobileAppsLinksTitle,

    // Feature Flags
    epgNoProgramText,
    epgEmptySlotText,
    epgDisplayHours,
    epgTimeFormat,
    epgDefaultImage,
    epgDisabledMessage,
    heroBanner,
    favoriteLimitation,
    favoriteLimitationMessage,
    continueWatchHistoryLimitation,

    // Legal
    redirectionPolicyURL,
    redirectionTosURL,
    redirectionAboutUsURL,
    redirectionSupportURL,
  };
};
